import { useState, useRef } from 'react'
import { supabase } from '@/lib/supabaseClient'

export default function ImageUpload({ onUploadComplete, folder = 'images', maxFiles = 5, existingImages = [] }) {
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [error, setError] = useState(null)
  const [dragActive, setDragActive] = useState(false)
  const fileInputRef = useRef(null)

  const uploadFile = async (file) => {
    const fileExt = file.name.split('.').pop()
    const fileName = `${Math.random().toString(36).substring(2)}_${Date.now()}.${fileExt}`
    const filePath = `${folder}/${fileName}`

    const { data, error } = await supabase.storage
      .from('project-images')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false,
      })

    if (error) throw error

    const { data: { publicUrl } } = supabase.storage
      .from('project-images')
      .getPublicUrl(data.path)

    return publicUrl
  }

  const handleFiles = async (files) => {
    setError(null)
    setUploading(true)
    setUploadProgress(0)

    try {
      const fileArray = Array.from(files)

      if (fileArray.length + existingImages.length > maxFiles) {
        throw new Error(`Maximum ${maxFiles} images allowed`)
      }

      for (const file of fileArray) {
        if (!file.type.startsWith('image/')) {
          throw new Error('Only image files are allowed')
        }

        if (file.size > 5 * 1024 * 1024) {
          throw new Error('File size must be less than 5MB')
        }
      }

      const urls = []
      for (let i = 0; i < fileArray.length; i++) {
        const url = await uploadFile(fileArray[i])
        urls.push(url)
        setUploadProgress(((i + 1) / fileArray.length) * 100)
      }

      onUploadComplete(urls)
      setUploadProgress(0)
    } catch (err) {
      setError(err.message)
      console.error('Upload error:', err)
    } finally {
      setUploading(false)
    }
  }

  const handleDrag = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files)
    }
  }

  const handleChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files)
    }
  }

  const handleClick = () => {
    fileInputRef.current?.click()
  }

  return (
    <div>
      <div
        className={`relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
          dragActive
            ? 'border-primary-600 bg-primary-50'
            : 'border-secondary-300 hover:border-primary-400'
        } ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept="image/*"
          multiple
          onChange={handleChange}
          disabled={uploading}
        />

        {uploading ? (
          <div>
            <div className="w-16 h-16 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-secondary-600 mb-2">Uploading images...</p>
            <div className="max-w-xs mx-auto bg-secondary-200 rounded-full h-2 overflow-hidden">
              <div
                className="bg-primary-600 h-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
            <p className="text-sm text-secondary-500 mt-2">{Math.round(uploadProgress)}%</p>
          </div>
        ) : (
          <div>
            <svg
              className="w-12 h-12 mx-auto mb-4 text-secondary-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
            <p className="text-secondary-700 font-medium mb-1">
              Drop images here or click to browse
            </p>
            <p className="text-sm text-secondary-500">
              PNG, JPG, WEBP up to 5MB ({existingImages.length}/{maxFiles} used)
            </p>
          </div>
        )}
      </div>

      {error && (
        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
          {error}
        </div>
      )}
    </div>
  )
}
